import { Button } from '@monorepo/shared/componentsV2/Button';
import { TextField } from '@monorepo/shared/componentsV2/fields/TextField';
import { BaseModal } from '@monorepo/shared/componentsV2/modals/BaseModal';
import { useToast } from '@monorepo/shared/contexts/ToastContext';
import {
  compose,
  isRequired as isRequiredValidator,
} from '@monorepo/shared/utils/validators';
import { DocumentFileResponse } from 'mapistry-shared';
import React, { useCallback, useMemo } from 'react';
import { Form } from 'react-final-form';
import { useOrganizationDocumentsList } from '../../hooks/useOrganizationDocumentsList';
import { useUpdateOrganizationDocument } from '../../hooks/useUpdateOrganizationDocument';

interface RenameDocumentModalProps {
  organizationId: string;
  document: DocumentFileResponse;
  onClose: () => void;
}

interface FormValues {
  name: string;
}

const isUniqueDocumentName =
  (documents: DocumentFileResponse[] | undefined, documentId: string) =>
  (value?: unknown) => {
    const trimmed = (value ?? '').toString().trim();
    if (!trimmed) return undefined;
    const isDuplicate = (documents ?? []).some((existing) => {
      if (existing.id === documentId) return false;
      return existing.name.toLowerCase() === trimmed.toLowerCase();
    });
    return isDuplicate
      ? 'A document with this name already exists in this folder. Names must be unique within a folder.'
      : undefined;
  };

export function RenameDocumentModal({
  organizationId,
  document,
  onClose,
}: RenameDocumentModalProps) {
  const { showUserFriendlyErrorToast, success } = useToast();

  const { documents } = useOrganizationDocumentsList({
    organizationId,
    folderId: document.folderId ?? undefined,
  });
  const [rename] = useUpdateOrganizationDocument({
    config: { throwOnError: true },
  });

  const nameValidator = useMemo(
    () =>
      compose(
        isRequiredValidator,
        isUniqueDocumentName(documents, document.id),
      ),
    [document.id, documents],
  );

  const onSubmit = useCallback(
    async (values: FormValues) => {
      const name = values.name.trim();
      try {
        await rename({
          organizationId,
          documentId: document.id,
          document: { name },
        });
        success(`Document was renamed to "${name}".`);
        onClose();
      } catch (err) {
        showUserFriendlyErrorToast(err, 'Unable to rename document.');
      }
    },
    [
      document.id,
      onClose,
      organizationId,
      rename,
      showUserFriendlyErrorToast,
      success,
    ],
  );

  return (
    <Form<FormValues>
      onSubmit={onSubmit}
      initialValues={{ name: document.name }}
      subscription={{
        submitting: true,
        pristine: true,
        hasValidationErrors: true,
      }}
    >
      {({ handleSubmit, submitting, pristine, hasValidationErrors }) => (
        <BaseModal
          open
          onClose={submitting ? undefined : onClose}
          showCloseButton
          title="Rename document"
          maxWidth="sm"
          buttons={[
            <Button
              key="cancel"
              variant="text"
              color="primary"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </Button>,
            <Button
              key="save"
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={submitting || pristine || hasValidationErrors}
            >
              {submitting ? 'Saving...' : 'Save'}
            </Button>,
          ]}
        >
          <TextField
            name="name"
            label="Document name"
            autoFocus
            required
            validate={nameValidator}
            fullWidth
            forceShowErrors={false}
          />
        </BaseModal>
      )}
    </Form>
  );
}
